import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { DependentSchema } from '@features/onboarding/schema/onboarding';
import { FieldErrors, UseFormReturn, Path } from 'react-hook-form';
import { DropdownFloating, DropdownFloatingSearch } from '@components/ui/dropdown-floating';
import { format, parseISO } from 'date-fns';

import { FormMessageSpan } from '@components/ui/form';

type DateKey = 'month' | 'day' | 'year';

const months = [
  { name: 'January', days: 31 },
  { name: 'February', days: 28 },
  { name: 'March', days: 31 },
  { name: 'April', days: 30 },
  { name: 'May', days: 31 },
  { name: 'June', days: 30 },
  { name: 'July', days: 31 },
  { name: 'August', days: 31 },
  { name: 'September', days: 30 },
  { name: 'October', days: 31 },
  { name: 'November', days: 30 },
  { name: 'December', days: 31 },
];

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

export const ApplicantFormDatepicker = ({
  idxForm,
  form,
  errors,
  month,
  day,
  year,
  onChange,
}: {
  idxForm: number;
  form: UseFormReturn<DependentSchema>;
  errors?: FieldErrors<DependentSchema['applicants'][number]['dateOfBirth']>;
  month: string;
  day: string;
  year: string;
  onChange: (key: DateKey, value: string) => void;
}) => {
  const [selectedMonth, setSelectedMonth] = useState(month);
  const [selectedDay, setSelectedDay] = useState(day);
  const [selectedYear, setSelectedYear] = useState(year);

  const currentYear = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear();
  const currentMonth = parseISO(format(new Date(), 'yyyy-MM-dd')).getMonth();
  const currentDay = parseISO(format(new Date(), 'yyyy-MM-dd')).getDate();

  const isFutureDate = (
    selectedYear: string,
    selectedMonth: string,
    selectedDay: string
  ): boolean => {
    const selectedDate = new Date(
      parseInt(selectedYear),
      months.findIndex((m) => m.name === selectedMonth),
      parseInt(selectedDay)
    );
    const currentDate = parseISO(format(new Date(), 'yyyy-MM-dd'));
    return selectedDate > currentDate;
  };

  const availableMonths = useMemo(() => {
    if (parseInt(selectedYear) === currentYear) {
      return months.slice(0, currentMonth + 1);
    }
    return months;
  }, [selectedYear]);

  const availableDays = useMemo(() => {
    const monthObj = months.find((m) => m.name === selectedMonth);
    if (!monthObj) return [];

    let daysInMonth = monthObj.days;

    if (selectedMonth === 'February' && isLeapYear(parseInt(selectedYear))) {
      daysInMonth = 29;
    }

    if (
      parseInt(selectedYear) === currentYear &&
      months.findIndex((m) => m.name === selectedMonth) === currentMonth
    ) {
      return Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString().padStart(2, '0')
      ).filter((day) => parseInt(day) <= currentDay);
    }

    return Array.from({ length: daysInMonth }, (_, i) =>
      (i + 1).toString().padStart(2, '0')
    );
  }, [selectedMonth, selectedYear]);

  const years = useMemo(() => {
    return Array.from({ length: 101 }, (_, i) => (2025 - i).toString()).filter(
      (year) => parseInt(year) <= currentYear
    );
  }, []);

  function handleChange(key: DateKey, value: string) {
    if (key === 'month') {
      setSelectedMonth(value);
    }
    if (key === 'day') {
      setSelectedDay(value);
    }
    if (key === 'year') {
      setSelectedYear(value);
    }

    onChange(key, value);
    form.trigger(
      `applicants.${idxForm}.dateOfBirth.${key}` as Path<DependentSchema>
    );
  }
  useEffect(() => {
    if (selectedYear && selectedMonth && selectedDay) {
      const isInvalid = isFutureDate(selectedYear, selectedMonth, selectedDay);
      if (isInvalid) {
        const currentDate = parseISO(format(new Date(), 'yyyy-MM-dd'));

        if (
          parseInt(selectedDay) > currentDay &&
          parseInt(selectedYear) === currentDate.getFullYear() &&
          months.findIndex((m) => m.name === selectedMonth) === currentMonth
        ) {
          setSelectedDay('');
          onChange('day', '');
          form.setValue(`applicants.${idxForm}.dateOfBirth.day`, '');
        } else {
          setSelectedDay('');
          onChange('day', '');
          form.setValue(`applicants.${idxForm}.dateOfBirth.day`, '');
        }
      }
    }
  }, [selectedYear, selectedMonth, selectedDay]);

  return (
    <div className="flex md:flex-row flex-col gap-3 w-full md:items-center max-md:gap-1">
      <span className="text-black text-xl flex items-center whitespace-nowrap lg:text-[22px] lg:tracking-tight h-13.5 lg:h-16.5 mb-auto">
        Date of Birth:
      </span>
      <div className="flex flex-row gap-4.5 w-full max-sm:gap-2">
        <div className="ml-2 max-md:ml-0 w-full flex flex-col">
          <DropdownFloatingSearch
            id={`month-select`}
            inputClass="border rounded-lg p-2 !text-[18px] md:!text-5 !h-13.5 xl:!h-16.5 w-full"
            chevronClass="max-xl:!-mr-1"
            placeholder="Select month"
            value={selectedMonth}
            onChange={(value) => handleChange('month', value)}
            options={availableMonths.map((m) => ({
              label: m.name,
              value: m.name,
            }))}
            label="Month"
            error={errors?.month && form.formState.isSubmitted}
          />
          {errors?.month && form.formState.isSubmitted && (
            <FormMessageSpan
              className="mt-2 text-sm text-center"
              text={errors.month.message}
            />
          )}
        </div>

        <div className="mdw-27 max-w-16 w-full min-w-[72px] flex flex-col">
          <DropdownFloatingSearch
            id={`day-select`}
            inputClass="border rounded-lg p-2 !text-[18px] md:!text-5 !h-13.5 xl:!h-16.5 w-full"
            chevronClass="max-xl:!-mr-1"
            placeholder="Select day"
            value={selectedDay}
            onChange={(value) => handleChange('day', value)}
            options={availableDays.map((d) => ({ label: d, value: d }))}
            label="Day"
            disabled={!availableDays.length}
            error={errors?.day && form.formState.isSubmitted}
          />
          {errors?.day && form.formState.isSubmitted && (
            <FormMessageSpan
              className="mt-2 text-sm text-center"
              text={errors.day.message}
            />
          )}
        </div>

        <div className="md:w-29 max-w-20 w-full min-w-20 flex flex-col">
          <DropdownFloatingSearch
            id={`year-select`}
            inputClass="border rounded-lg p-2 !text-[18px] md:!text-5 !h-13.5 xl:!h-16.5 w-full"
            chevronClass="max-xl:!-mr-1"
            placeholder="Select year"
            value={selectedYear}
            onChange={(value) => handleChange('year', value)}
            options={years.map((y) => ({ label: y, value: y }))}
            label="Year"
            error={errors?.year && form.formState.isSubmitted}
          />
          {errors?.year && form.formState.isSubmitted && (
            <FormMessageSpan
              className="mt-2 text-sm text-center"
              text={errors.year.message}
            />
          )}
        </div>
      </div>
    </div>
  );
};
